import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Botao, Cartao } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { useSessao } from '../../nucleo/sessao';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dinheiro, nomePessoa } from '../../nucleo/formato';
import { Barra, BotaoExportar, Kpi, LinhaEscopo, NomeParceiro, percentualTexto, Rolagem, TD, TH, Valor } from './comum';
import { FAIXAS, type Faixa, type Inadimplencia as InadApi, type TituloAging } from './tipos';

/**
 * E1 (resposta 3 do Gustavo): "Enviar ao cliente" pelo Portal, como fotografia. O servidor gera o CSV da posição financeira
 * só deste parceiro (filtro forçado e conferido) e publica como entrega na área Financeiro; o cliente é avisado por push e
 * e-mail. Só com o Portal instalado, `portal.publicar` e `relatorios.exportar`.
 */
function BotaoEnviarCliente({ parceiro, nome }: { parceiro: string; nome: string }) {
  const { t } = useTranslation('relatorios');
  const s = useSessao();
  const toast = useToast();
  const [confirmar, setConfirmar] = useState(false);
  const m = useMutation({
    mutationFn: () => post<{ titulos: number }>('/relatorios/cliente/envios', { id: crypto.randomUUID(), parceiro_id: parceiro }),
    onSuccess: (r) => {
      setConfirmar(false);
      toast.sucesso(t('cliente.enviado', { count: r.titulos }));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (!s.instalado('portal') || !s.pode('portal.publicar') || !s.pode('relatorios.exportar')) return null;
  return (
    <>
      <Botao variante="secundario" icone={<Send className="size-4" aria-hidden />} onClick={() => setConfirmar(true)}>
        {t('cliente.enviar')}
      </Botao>
      <Confirmar aberto={confirmar} aoFechar={() => setConfirmar(false)} aoConfirmar={() => m.mutate()} titulo={t('cliente.enviar')} texto={t('cliente.confirmar', { nome })} carregando={m.isPending} />
    </>
  );
}

function Titulos({ parceiro, nome, faixa, aoFechar }: { parceiro: string | null; nome: string; faixa?: Faixa; aoFechar: () => void }) {
  const { t } = useTranslation('relatorios');
  const { t: tn } = useTranslation();
  const params = { parceiro_id: parceiro ?? undefined, faixa, limite: 500 };
  const q = useQuery({ queryKey: ['/relatorios/inadimplencia/titulos', params], queryFn: () => get<{ itens: TituloAging[]; total: number; truncado: boolean }>('/relatorios/inadimplencia/titulos', params) });
  return (
    <Modal
      aberto
      largura="lg"
      aoFechar={aoFechar}
      titulo={t('inadimplencia.titulosDe', { nome })}
      rodape={
        <>
          {parceiro && <BotaoEnviarCliente parceiro={parceiro} nome={nome} />}
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.fechar')}
          </Botao>
        </>
      }
    >
      <Estado consulta={q}>
        {(d) => (
          <div className="flex min-w-0 flex-col gap-3">
            <div tabIndex={0} role="region" aria-label={t('comum.tabela')} className="max-h-96 overflow-auto rounded-lg border border-borda focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-sup-1">
                  <tr className="border-b border-borda text-left text-tinta-3">
                    <th scope="col" className="px-3 py-2 font-medium">{t('inadimplencia.numero')}</th>
                    <th scope="col" className="px-3 py-2 font-medium">{t('inadimplencia.vencimento')}</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">{t('inadimplencia.atraso')}</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">{t('inadimplencia.principal')}</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">{t('inadimplencia.encargos')}</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">{t('inadimplencia.atualizado')}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.itens.map((i) => (
                    <tr key={i.id} className="border-b border-borda last:border-0">
                      <td className="numero px-3 py-2 whitespace-nowrap">{i.numero}</td>
                      <td className="numero px-3 py-2 whitespace-nowrap text-tinta-2">{data(i.vencimento)}</td>
                      <td className="numero px-3 py-2 text-right whitespace-nowrap">{i.dias > 0 ? t('inadimplencia.dias', { count: i.dias }) : t('inadimplencia.faixas.a_vencer')}</td>
                      <td className="px-3 py-2 text-right">
                        <Valor centavos={i.principal_centavos} moeda={i.moeda} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Valor centavos={i.juros_centavos + i.multa_centavos} moeda={i.moeda} />
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        <Valor centavos={i.atualizado_centavos} moeda={i.moeda} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {d.truncado && <p className="text-xs text-tinta-3">{t('detalhe.mostrando', { n: d.itens.length, total: d.total })}</p>}
          </div>
        )}
      </Estado>
    </Modal>
  );
}

/** R4 · Inadimplência (aging): faixas, valor atualizado com encargos estimados, créditos e por cliente. */
export function Inadimplencia() {
  const { t } = useTranslation('relatorios');
  const q = useQuery({ queryKey: ['/relatorios/inadimplencia'], queryFn: () => get<InadApi>('/relatorios/inadimplencia'), placeholderData: keepPreviousData });
  const [aberto, setAberto] = useState<{ parceiro: string | null; nome: string; faixa?: Faixa } | null>(null);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Barra>
        <BotaoExportar relatorio="inadimplencia" parametros={{}} />
      </Barra>
      <Estado consulta={q}>
        {(d) => (
          <>
            <div>
              <LinhaEscopo escopo={d.escopo} />
              <p className="text-sm text-tinta-3">{t('inadimplencia.referencia', { data: data(d.em) })}</p>
            </div>
            {d.por_moeda.length === 0 ? (
              <Cartao>
                <Vazio titulo={t('inadimplencia.vazioTitulo')} texto={t('inadimplencia.vazioTexto')} />
              </Cartao>
            ) : (
              d.por_moeda.map((m) => (
                <section key={m.moeda} aria-label={t('comum.moeda', { moeda: m.moeda })} className="flex min-w-0 flex-col gap-4">
                  {d.por_moeda.length > 1 && <h2 className="text-sm font-semibold">{t('comum.moeda', { moeda: m.moeda })}</h2>}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
                    <Kpi rotulo={t('inadimplencia.aVencer')} cor="text-ciano" valor={dinheiro(m.a_vencer_centavos, m.moeda)} />
                    <Kpi rotulo={t('inadimplencia.vencido')} cor="text-perigo" valor={dinheiro(m.vencido_centavos, m.moeda)} />
                    <Kpi rotulo={t('inadimplencia.encargos')} cor="text-laranja" valor={dinheiro(m.total.juros_centavos + m.total.multa_centavos, m.moeda)} />
                    <Kpi rotulo={t('inadimplencia.atualizado')} valor={dinheiro(m.total.atualizado_centavos, m.moeda)} detalhe={m.creditos_centavos ? `${t('inadimplencia.creditos')}: ${dinheiro(m.creditos_centavos, m.moeda)}` : undefined} />
                    <Kpi rotulo={t('inadimplencia.percentual')} cor={m.vencido_centavos > 0 ? 'text-perigo' : 'text-tinta-1'} valor={percentualTexto(m.percentual_inadimplencia)} />
                  </div>
                  <Cartao titulo={t('inadimplencia.porFaixa')} semPadding>
                    <Rolagem rotulo={`${t('inadimplencia.porFaixa')} (${m.moeda})`}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-borda text-left text-tinta-3">
                            <th scope="col" className={TH}>{t('inadimplencia.porFaixa')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('inadimplencia.documentos')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('inadimplencia.principal')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('inadimplencia.juros')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('inadimplencia.multa')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('inadimplencia.atualizado')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {m.faixas.map((f) => (
                            <tr key={f.faixa} className="border-b border-borda last:border-0">
                              <th scope="row" className={`${TD} text-left font-normal`}>
                                {f.documentos > 0 ? (
                                  <button type="button" onClick={() => setAberto({ parceiro: null, nome: t(`inadimplencia.faixas.${f.faixa}`), faixa: f.faixa })} className="text-left underline-offset-2 hover:text-acento-forte hover:underline">
                                    {t(`inadimplencia.faixas.${f.faixa}`)}
                                  </button>
                                ) : (
                                  t(`inadimplencia.faixas.${f.faixa}`)
                                )}
                              </th>
                              <td className={`${TD} numero text-right`}>{f.documentos}</td>
                              <td className={`${TD} text-right`}>
                                <Valor centavos={f.principal_centavos} moeda={m.moeda} />
                              </td>
                              <td className={`${TD} text-right`}>
                                <Valor centavos={f.juros_centavos} moeda={m.moeda} />
                              </td>
                              <td className={`${TD} text-right`}>
                                <Valor centavos={f.multa_centavos} moeda={m.moeda} />
                              </td>
                              <td className={`${TD} text-right font-medium`}>
                                <Valor centavos={f.atualizado_centavos} moeda={m.moeda} />
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-sup-2 font-semibold">
                            <th scope="row" className={`${TD} text-left`}>{t('comum.total')}</th>
                            <td className={`${TD} numero text-right`}>{m.total.documentos}</td>
                            <td className={`${TD} text-right`}>
                              <Valor centavos={m.total.principal_centavos} moeda={m.moeda} />
                            </td>
                            <td className={`${TD} text-right`}>
                              <Valor centavos={m.total.juros_centavos} moeda={m.moeda} />
                            </td>
                            <td className={`${TD} text-right`}>
                              <Valor centavos={m.total.multa_centavos} moeda={m.moeda} />
                            </td>
                            <td className={`${TD} text-right`}>
                              <Valor centavos={m.total.atualizado_centavos} moeda={m.moeda} />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </Rolagem>
                  </Cartao>
                  <Cartao titulo={t('inadimplencia.porCliente')} semPadding>
                    <Rolagem rotulo={`${t('inadimplencia.porCliente')} (${m.moeda})`}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-borda text-left text-tinta-3">
                            <th scope="col" className={TH}>{t('inadimplencia.cliente')}</th>
                            {FAIXAS.map((f) => (
                              <th key={f} scope="col" className={`${TH} text-right`}>{t(`inadimplencia.faixas.${f}`)}</th>
                            ))}
                            <th scope="col" className={`${TH} text-right`}>{t('inadimplencia.atualizado')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('inadimplencia.creditos')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('inadimplencia.liquido')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('inadimplencia.maisAntigo')}</th>
                            <th scope="col" className={`${TH} text-right`}>{t('inadimplencia.atrasoMedio')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {m.clientes.map((c) => (
                            <tr key={c.parceiro_id ?? 'sem'} className="border-b border-borda last:border-0">
                              <th scope="row" className={`${TD} max-w-56 text-left font-normal`}>
                                <button type="button" onClick={() => setAberto({ parceiro: c.parceiro_id, nome: c.nome ? nomePessoa(c.nome) : t('comum.semNome') })} className="text-left break-words underline-offset-2 hover:text-acento-forte hover:underline">
                                  <NomeParceiro nome={c.nome} vazio={t('comum.semNome')} />
                                </button>
                              </th>
                              {FAIXAS.map((f) => (
                                <td key={f} className={`${TD} text-right ${f === 'a_vencer' ? '' : c.faixas[f] ? 'text-perigo' : 'text-tinta-3'}`}>
                                  {c.faixas[f] ? <Valor centavos={c.faixas[f]} moeda={m.moeda} /> : ''}
                                </td>
                              ))}
                              <td className={`${TD} text-right font-medium`}>
                                <Valor centavos={c.atualizado_centavos} moeda={m.moeda} />
                              </td>
                              <td className={`${TD} text-right`}>{c.creditos_centavos ? <Valor centavos={-c.creditos_centavos} moeda={m.moeda} /> : ''}</td>
                              <td className={`${TD} text-right`}>
                                <Valor centavos={c.liquido_centavos} moeda={m.moeda} />
                              </td>
                              <td className={`${TD} numero text-right whitespace-nowrap`}>{c.dias_mais_antigo ? t('inadimplencia.dias', { count: c.dias_mais_antigo }) : ''}</td>
                              <td className={`${TD} numero text-right whitespace-nowrap`}>{c.atraso_medio_dias ? t('inadimplencia.dias', { count: c.atraso_medio_dias }) : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Rolagem>
                    <p className="border-t border-borda px-4 py-3 text-xs text-tinta-3">{t('inadimplencia.nota')}</p>
                  </Cartao>
                </section>
              ))
            )}
          </>
        )}
      </Estado>
      {aberto && <Titulos parceiro={aberto.parceiro} nome={aberto.nome} faixa={aberto.faixa} aoFechar={() => setAberto(null)} />}
    </div>
  );
}
